import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, MessageCircle, Mic, Sparkles, Brain, ArrowRight, ShieldCheck, Flame, ScanLine } from "lucide-react";
import { usePlan, PlanPrice } from "@/lib/plan";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const FEATURES = [
  { id: "loop", name: "LanguageLoop", desc: "landing.descLoop", icon: Camera, bg: "bg-mint", tag: "landing.freeTag" },
  { id: "friend", name: "Fluent Friend", desc: "landing.descFriend", icon: MessageCircle, bg: "bg-lavender" },
  { id: "lens", name: "FluentLens", desc: "landing.descLens", icon: ScanLine, bg: "bg-peach" },
  { id: "journal", nameKey: "landing.featJournal", desc: "landing.descJournal", icon: Mic, bg: "bg-sky" },
  { id: "today", nameKey: "landing.featToday", desc: "landing.descToday", icon: Sparkles, bg: "bg-butter" },
];

export default function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const plan = usePlan();
  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <span className="font-cabinet font-extrabold text-2xl">GetFluent<span className="text-coral">Loop</span></span>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="nb-btn-primary px-5 py-2 text-sm" data-testid="landing-dashboard-btn">{t("home.dashboard")}</Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline font-cabinet font-bold text-sm hover:text-coral" data-testid="landing-login-link">{t("home.signIn")}</Link>
                <Link to="/signup" className="nb-btn-primary px-5 py-2 text-sm" data-testid="landing-signup-btn">{t("home.getStarted")}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="nb-tag bg-mint mb-5" data-testid="landing-badge">{t("home.badge")}</div>
            <h1 className="text-5xl sm:text-6xl font-cabinet font-extrabold leading-[1.05] tracking-tight">
              {t("home.heroPre")}<br />{t("home.heroPost")} <span className="bg-coral text-white px-2 -rotate-1 inline-block border-2 border-ink">{t("home.heroWord")}</span>.
            </h1>
            <p className="text-lg text-gray-700 mt-6 max-w-md">
              {t("landing.heroSub")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/app" className="nb-btn-primary px-8 py-4 text-lg" data-testid="hero-try-btn">
                <Camera className="w-5 h-5" /> {t("home.tryFree")}
              </Link>
              <Link to="/signup" className="nb-btn-outline px-8 py-4 text-lg" data-testid="hero-signup-btn">
                {t("home.startLearning")} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
              </Link>
            </div>
            <p className="text-xs text-gray-500 mt-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> {t("home.privacy")}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }} className="relative">
            <div className="nb-card p-0 overflow-hidden shadow-hard-lg rotate-2">
              <img src="https://images.unsplash.com/photo-1758273237903-f176384eadbb?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                alt={t("landing.imgAlt")} className="w-full h-72 object-cover border-b-2 border-ink" />
              <div className="p-5 bg-mint">
                <div className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal">{t("landing.mockLearned")}</div>
                <div className="font-cabinet font-extrabold text-4xl mt-1">el árbol 🌳</div>
                <div className="text-sm font-medium mt-1"><span dir="ltr" lang="es">"El árbol es muy alto."</span> — {t("landing.mockGloss")}</div>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 nb-card bg-butter px-4 py-3 -rotate-3 flex items-center gap-2">
              <Flame className="w-5 h-5" /> <span className="font-cabinet font-bold">{t("landing.streakBadge", { count: 7 })}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl sm:text-4xl font-cabinet font-extrabold text-center">{t("home.bundlesTitle")}</h2>
        <p className="text-center text-gray-600 mt-3 max-w-xl mx-auto">{t("home.bundlesSub")}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {FEATURES.map((b, i) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className={`nb-card ${b.bg} p-6 relative`}>
              {b.tag && <span className="absolute top-4 end-4 nb-tag bg-coral text-white">{t(b.tag)}</span>}
              <div className="w-12 h-12 rounded-xl border-2 border-ink bg-white flex items-center justify-center">
                <b.icon className="w-6 h-6 stroke-[2.5]" />
              </div>
              <h3 className="font-cabinet font-bold text-xl mt-4">{b.nameKey ? t(b.nameKey) : b.name}</h3>
              <p className="text-sm text-gray-700 mt-1">{t(b.desc)}</p>
            </motion.div>
          ))}
          <div className="nb-card bg-ink text-white p-6 flex flex-col justify-center">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-300" data-testid="landing-plan-name">{plan?.name || "GetFluentLoop Pro"}</div>
            <PlanPrice plan={plan} className="font-cabinet font-extrabold text-4xl mt-1" />
            <p className="text-sm text-gray-300 mt-2">{t("home.priceSub")}</p>
            <Link to="/signup" className="nb-btn-mint mt-4 py-2 text-sm">{t("home.startNow")}</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
